import { EnvironmentProviders, inject, Provider } from '@angular/core';
import {
  provideSearchModule,
  SHELL_SEARCH_ACTION,
  ShellSearchAction,
} from '@softapps/core';
import { SOFTLINE_CONFIG_SETTINGS } from '@softline/application';
import { provideCustomerInfoDetailPanel } from '@softapps/core/customer-info';
import { SalesDocumentSearchResultComponent } from './components/sales-document-search-result/sales-document-search-result.component';
import SalesConfigComponent from './pages/config/sales-config.component';
import { DocumentTypeRepository } from './repositories/document-type.repository';
import {
  DocumentPreviewRepository,
  DocumentPreviewService,
} from '@softapps/wws/core/documents';
import { SalesDocumentRepository } from './repositories/sales-document.repository';
import {
  DEFAULT_SALES_CONFIG,
  SalesModuleConfig,
  SOFTLINE_CONFIG_WWS_SALES,
  SOFTLINE_PERMISSION_WWS_SALES,
} from './sales.shared';
import { DocumentFromTemplateService } from './services/document-from-template.service';
import { DocumentTypeService } from './services/document-type.service';
import { SalesConfigService } from './services/sales-config.service';
import { SalesDocumentSearchProvider } from './services/sales-document-search.provider';
import { restoreSalesDocumentHit } from './services/sales-document-search.types';
import { SalesDocumentService } from './services/sales-document.service';
import { SalesOverviewStateService } from './services/sales-overview-state.service';
import { SalesQuickCreateService } from './services/sales-quick-create.service';

// Cross-cutting/module-level providers WITHOUT the menu/i18n registration and
// without the routes graph — composed at boot by both provideSalesModule
// (monolith path) and the remote's ./ModuleGlobals entry (federation path).
// Must not import the routes file.
export function provideSalesGlobals(
  config?: SalesModuleConfig
): Array<Provider | EnvironmentProviders> {
  return [
    {
      provide: SOFTLINE_CONFIG_SETTINGS,
      multi: true,
      useFactory: () => {
        const configService = inject(SalesConfigService);
        return {
          key: 'SOFTLINE_SETTINGS_SALES',
          component: SalesConfigComponent,
          title: '#SALES.CONFIG.TITLE',
          default: undefined,
          icon: 'fa-regular fa-cart-shopping',
          group: 'module',
          permission: SOFTLINE_PERMISSION_WWS_SALES,
          visible: true,
          onReset: () => configService.reset(),
        };
      },
    },
    {
      provide: SOFTLINE_CONFIG_WWS_SALES,
      useFactory: () => {
        const stored = inject(SalesConfigService).config();
        return { ...DEFAULT_SALES_CONFIG, ...config, ...stored };
      },
    },
    { provide: SalesDocumentRepository, useClass: SalesDocumentRepository },
    SalesDocumentService,
    { provide: DocumentPreviewRepository, useClass: DocumentPreviewRepository },
    DocumentPreviewService,
    SalesOverviewStateService,
    { provide: DocumentTypeRepository, useClass: DocumentTypeRepository },
    DocumentTypeService,
    {
      provide: SHELL_SEARCH_ACTION,
      multi: true,
      useFactory: (): ShellSearchAction => {
        const service = inject(SalesQuickCreateService);
        return {
          title: 'Neuen Beleg erstellen',
          category: 'Verkauf',
          icon: 'fa-regular fa-file-plus',
          permission: SOFTLINE_PERMISSION_WWS_SALES,
          url: '/sales',
          action: () => service.createDocument(),
        };
      },
    },
    {
      provide: SHELL_SEARCH_ACTION,
      multi: true,
      useFactory: (): ShellSearchAction => {
        const service = inject(DocumentFromTemplateService);
        return {
          title: 'Beleg aus Vorlage erstellen',
          category: 'Verkauf',
          icon: 'fa-regular fa-copy',
          permission: SOFTLINE_PERMISSION_WWS_SALES,
          url: '/sales',
          action: () => service.create(),
        };
      },
    },
    provideCustomerInfoDetailPanel(),
    provideSearchModule({
      category: 'Belege',
      factory: () => inject(SalesDocumentSearchProvider),
      component: SalesDocumentSearchResultComponent,
      icon: 'fa-regular fa-file-lines',
      permission: SOFTLINE_PERMISSION_WWS_SALES,
      url: '/sales',
      restore: restoreSalesDocumentHit,
    }),
  ];
}
